import React, { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { AIChatView } from '../../components/ai/AIChatView';
import { useEditLastMessage } from '../../components/ai/hooks/useEditLastMessage';
import type { ChatMessage } from '../../components/ai/types';
import type {
  AIConfig,
  CharacterBook,
  CharacterSpec,
  ChatOwnerType,
  PromptSettings,
  SamplerSettings,
} from '../../db/characterTypes';
import { stripFences } from '../core/stripFences';
import { computeCharacterAgentContextUsage, usageStatus } from '../hosts/character/contextUsage';
import { AgentReviewModal } from '../review/AgentReviewModal';
import { applyCharacterReview, diffCharacterReview, formatReviewOutcome } from '../review/diff';
import type { CharacterReviewPayload } from '../review/types';
import type { ReviewDecisions } from '../review/types';
import { AgentChatMessage } from './AgentChatMessage';
import { AgentReviewStatus } from './AgentReviewStatus';
import { AgentToolModeChip } from './AgentToolModeChip';
import { formatAgentBusyLabel } from './busyLabel';
import { LiveSpeech } from './LiveSpeech';
import { LiveThinking } from './LiveThinking';
import {
  CHARACTER_LOOKUP_TOOLS,
  CONTINUE_MESSAGE,
  interruptedRunMessageIds,
  messageNotices,
  shouldRenderAgentMessage,
  visibleToolEvents,
  writeRecapLine,
} from './notices';
import { contextMeterHistory, keptEditsOptions } from './loopHistory';
import { characterMentions } from './mentions';
import { characterSuggestions } from './suggestions';
import type { AgentToolTarget } from './types';
import { useCharacterAgent, type PersistAgentCard } from './useCharacterAgent';
import { useLeaveWarning } from './useLeaveWarning';

export interface CharacterAgentChatProps {
  aiConfig: AIConfig;
  samplerSettings: SamplerSettings;
  promptSettings: PromptSettings;
  getSpec: () => CharacterSpec;
  getBook: () => CharacterBook;
  persist: PersistAgentCard;
  getCustomContext: () => Promise<string | null>;
  flushDraft: () => void | Promise<void>;
  takeSnapshot: () => Promise<void>;
  customContextIncluded: boolean;
  customContextCharLength?: number;
  headerLeading?: ReactNode;
  headerActions?: ReactNode;
  onClose?: () => void;
  onRunningChange?: (running: boolean) => void;
  onOpenTarget?: (target: AgentToolTarget) => void;
  requireReview?: boolean;
  keepEdits?: boolean;
  chatOwnerType: ChatOwnerType;
  chatOwnerId: string;
}

export function CharacterAgentChat({
  aiConfig,
  samplerSettings,
  promptSettings,
  getSpec,
  getBook,
  persist,
  getCustomContext,
  flushDraft,
  takeSnapshot,
  customContextIncluded,
  customContextCharLength = 0,
  headerLeading,
  headerActions,
  onClose,
  onRunningChange,
  onOpenTarget,
  requireReview = false,
  keepEdits = false,
  chatOwnerType,
  chatOwnerId,
}: CharacterAgentChatProps): React.ReactElement {
  const [review, setReview] = useState<CharacterReviewPayload | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [isApplyingReview, setIsApplyingReview] = useState(false);
  const shouldReview = useCallback(() => requireReview, [requireReview]);
  const holdRunRef = useRef<() => void>(() => undefined);

  const handlePendingReview = useCallback((pending: CharacterReviewPayload) => {
    if (diffCharacterReview(pending).length === 0) return;
    holdRunRef.current();
    setReview(pending);
    setReviewOpen(true);
  }, []);

  const session = useCharacterAgent({
    aiConfig,
    samplerSettings,
    promptSettings,
    getSpec,
    getBook,
    persist,
    getCustomContext,
    flushDraft,
    takeSnapshot,
    shouldReview,
    onPendingReview: handlePendingReview,
    onRunningChange,
    chatOwnerType,
    chatOwnerId,
    keepEditsInContext: keepEdits,
  });
  const { noteReviewOutcome, holdRunForReview, noteRenumberedEntries } = session;
  useEffect(() => {
    holdRunRef.current = holdRunForReview;
  }, [holdRunForReview]);
  useLeaveWarning(session.isProcessing || review != null);

  const handleApplyReview = useCallback(
    async (decisions: ReviewDecisions) => {
      if (!review) return;
      const outcome = formatReviewOutcome(diffCharacterReview(review), decisions);
      const staged = applyCharacterReview(review, decisions);
      if (!staged.spec && !staged.book) {
        noteReviewOutcome(outcome);
        setReview(null);
        setReviewOpen(false);
        return;
      }
      setIsApplyingReview(true);
      try {
        await takeSnapshot();
        const renumbered = new Map<number, number>();
        await persist((latest) => {
          renumbered.clear();
          return applyCharacterReview(review, decisions, latest, renumbered);
        });
        noteRenumberedEntries(renumbered);
        noteReviewOutcome(formatReviewOutcome(diffCharacterReview(review), decisions, renumbered));
      } finally {
        setIsApplyingReview(false);
        setReview(null);
        setReviewOpen(false);
      }
    },
    [noteRenumberedEntries, noteReviewOutcome, persist, review, takeSnapshot],
  );

  const handleDiscardReview = useCallback(() => {
    noteReviewOutcome(formatReviewOutcome([], null));
    setReview(null);
    setReviewOpen(false);
  }, [noteReviewOutcome]);

  const handleAskGuarded = useCallback(
    (question: string) => {
      if (review) {
        setReviewOpen(true);
        return Promise.resolve();
      }
      return session.handleAsk(question);
    },
    [review, session],
  );

  const handleContinue = useCallback(() => {
    void handleAskGuarded(CONTINUE_MESSAGE);
  }, [handleAskGuarded]);

  const handleRegenerateGuarded = useCallback(() => {
    if (review) {
      setReviewOpen(true);
      return Promise.resolve();
    }
    return session.handleRegenerate();
  }, [review, session]);

  // The review outcome is noted on the last agent reply, so deleting replies would move it to an earlier run.
  const handleDeleteGuarded = useCallback(
    (messageId: string) => {
      if (review) {
        setReviewOpen(true);
        return;
      }
      session.handleDeleteMessage(messageId);
    },
    [review, session],
  );

  const { lastUserIndex, editingId, startEdit, cancelEdit, commitEdit } = useEditLastMessage(
    session.chatHistory,
    session.handleDeleteMessage,
  );

  const handleEditGuarded = useCallback(
    (message: ChatMessage, setComposerText: (text: string) => void) => {
      if (review) {
        setReviewOpen(true);
        return;
      }
      startEdit(message, setComposerText);
    },
    [review, startEdit],
  );

  const handleComposerAsk = useCallback(
    (question: string) => {
      if (!review) commitEdit();
      return handleAskGuarded(question);
    },
    [commitEdit, handleAskGuarded, review],
  );

  const contextLabels = useMemo(() => {
    const labels = ['Field catalog', 'Entry catalog'];
    if (customContextIncluded) labels.unshift('Custom context');
    return labels;
  }, [customContextIncluded]);

  // While a live prompt count is pinned, the idle estimate is never shown — skip
  // the catalog rebuild + full-history encode that would otherwise run per commit.
  const contextUsage = useMemo(() => {
    if (session.livePromptTokens != null) {
      const limit = Math.max(1, samplerSettings.contextLength || 0);
      const percentage = Math.min(100, (session.livePromptTokens / limit) * 100);
      return {
        tokens: session.livePromptTokens,
        limit,
        percentage,
        status: usageStatus(percentage),
      };
    }
    return computeCharacterAgentContextUsage({
      spec: getSpec(),
      book: getBook(),
      customContextCharLength,
      customContextIncluded,
      history: contextMeterHistory(
        session.chatHistory,
        session.toolEventsByMessageId,
        CHARACTER_LOOKUP_TOOLS,
        session.errorByMessageId,
        keptEditsOptions(keepEdits, session.toolMode, samplerSettings),
      ),
      contextLength: samplerSettings.contextLength,
    });
  }, [
    customContextCharLength,
    customContextIncluded,
    getBook,
    getSpec,
    keepEdits,
    samplerSettings,
    session.chatHistory,
    session.errorByMessageId,
    session.livePromptTokens,
    session.toolEventsByMessageId,
    session.toolMode,
  ]);

  const lostMessageIds = useMemo(
    () => interruptedRunMessageIds(session.chatHistory, session.toolEventsByMessageId),
    [session.chatHistory, session.toolEventsByMessageId],
  );

  const renderMessage = useCallback(
    (message: ChatMessage, index: number, setComposerText: (text: string) => void) => {
      const events = session.toolEventsByMessageId[message.id] ?? [];
      const notices = messageNotices(session.errorByMessageId[message.id], events);
      const toolEvents = visibleToolEvents(events, CHARACTER_LOOKUP_TOOLS);
      const writesLost = lostMessageIds.has(message.id);
      const speech = message.role === 'assistant' ? stripFences(message.content) : message.content;
      const recapLine = speech ? null : writeRecapLine(toolEvents, writesLost);
      const showReasoning = aiConfig.showReasoning ?? true;
      if (
        !shouldRenderAgentMessage(
          message.role,
          speech,
          toolEvents,
          notices,
          showReasoning ? message.reasoning ?? '' : '',
        )
      ) {
        return null;
      }
      return (
        <AgentChatMessage
          message={message}
          messageIndex={index}
          chatHistoryLength={session.chatHistory.length}
          isProcessing={session.isProcessing}
          showReasoning={showReasoning}
          showRegenerate
          notices={notices}
          toolEvents={toolEvents}
          writesLost={writesLost}
          recapLine={recapLine}
          onRegenerate={handleRegenerateGuarded}
          onDelete={handleDeleteGuarded}
          onOpenTarget={onOpenTarget}
          onContinue={
            index === session.chatHistory.length - 1 && !session.isProcessing
              ? handleContinue
              : undefined
          }
          onEdit={
            index === lastUserIndex
              ? () => handleEditGuarded(message, setComposerText)
              : undefined
          }
          onCancelEdit={
            message.id === editingId ? () => cancelEdit(setComposerText) : undefined
          }
        />
      );
    },
    [
      aiConfig.showReasoning,
      cancelEdit,
      editingId,
      handleContinue,
      handleDeleteGuarded,
      handleEditGuarded,
      handleRegenerateGuarded,
      lastUserIndex,
      lostMessageIds,
      onOpenTarget,
      session.chatHistory.length,
      session.errorByMessageId,
      session.isProcessing,
      session.toolEventsByMessageId,
    ],
  );

  const reviewChanges = useMemo(
    () => (review ? diffCharacterReview(review) : []),
    [review],
  );
  const hasPendingReview = review != null;

  return (
    <>
    <AIChatView
      title="Character agent"
      emptyTitle="Character agent"
      emptyBody="Ask it to fill or revise this card from custom context. It writes spec fields, greetings, and the embedded lorebook. Use Snapshots if you need to roll back."
      placeholder="Tell the agent what to write…"
      contextLabels={contextLabels}
      contextEmptyHint="Custom context is optional. Enable it in the AI Context panel to give the agent source notes."
      composerHint="Stop, then Send to retry · Writes go into this card"
      composerDisabled={hasPendingReview}
      composerDisabledPlaceholder="Pending agent writes. Accept or reject via the yellow Review button…"
      composerDisabledHint="Review pending. Accept or reject via the yellow Review button above"
      headerLeading={headerLeading}
      headerActions={
        <>
          <AgentToolModeChip mode={session.toolMode} />
          <AgentReviewStatus
            requireReview={requireReview}
            pendingCount={review && !reviewOpen ? reviewChanges.length : null}
            onOpen={() => setReviewOpen(true)}
          />
          {headerActions}
        </>
      }
      showReasoning={false}
      showRegenerate
      showStreamDraft={false}
      contextUsage={contextUsage}
      chatHistory={session.chatHistory}
      isProcessing={session.isProcessing}
      error={session.chatHistory.length === 0 ? session.error : null}
      isStreaming={session.isStreaming}
      streamingContent={session.streamingContent}
      streamingReasoning={session.streamingReasoning}
      handleAsk={handleComposerAsk}
      handleRegenerate={handleRegenerateGuarded}
      handleNewChat={session.handleNewChat}
      handleDeleteMessage={handleDeleteGuarded}
      handleAbort={session.handleAbort}
      clearError={session.clearError}
      isHydrating={session.isHydrating}
      hasOlderMessages={session.hasOlderMessages}
      onLoadOlder={session.handleLoadOlder}
      onClose={onClose}
      emptySuggestions={characterSuggestions(getSpec(), getBook(), customContextIncluded)}
      mentionOptions={() => characterMentions(getSpec(), getBook())}
      renderMessage={renderMessage}
      processingIndicator={
        <div className="py-1 text-fg-muted">
          <div className="flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
            <span className="text-xs">
              {session.busyAction
                ? formatAgentBusyLabel(session.busyAction, getBook().entries)
                : 'Working…'}
            </span>
          </div>
          {aiConfig.showReasoning !== false ? (
            <LiveThinking text={session.streamingReasoning} />
          ) : null}
          <LiveSpeech text={session.streamingContent} isStreaming={session.isStreaming} />
        </div>
      }
    />
    {review && reviewOpen && (
      <AgentReviewModal
        changes={reviewChanges}
        isApplying={isApplyingReview}
        onApply={(decisions) => void handleApplyReview(decisions)}
        onDiscard={handleDiscardReview}
        onMinimize={() => setReviewOpen(false)}
      />
    )}
    </>
  );
}
